import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "@/lib/toast";
import { supabase } from "@/integrations/supabase/client";
import { Icon } from "@/components/primitives";
import {
  MARKETPLACE_NAMES,
  ensureUrlProtocol,
  isValidUrl,
  parseMarketplaceLinks,
  urlDomainWarning,
  urlPrefixFor,
  type MarketplaceLink,
} from "@/lib/marketplaces";

// ─────────────────────────────────────────────────────────────────────────────
// ListingStatusSheet — owner-only bottom-sheet for managing a posted scan's
// listing status. Three actions:
//   1. Add / edit / remove marketplace links (each = name + optional URL)
//   2. Implicit "Kept" state when the marketplace list is empty
//   3. Unpost — flip posted=false so the scan disappears from the public
//      feed but stays in the owner's closet (reactions/comments preserved)
//
// Writes go straight to the `scans` row. RLS already restricts UPDATE to the
// owner, so the only contract here is that `scanId` belongs to the signed-in
// user — the parent guarantees that by passing `isOwner` to ListingBadge.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  scanId: string | null;
  scanTitle?: string;
  initialLinks: MarketplaceLink[];
  /** Current deadstock state — drives the "It's deadstock" checkbox. */
  initialDeadstock?: boolean;
  /** Current sold metadata, if any. When set the sheet shows the "sold" state. */
  initialSold?: { soldAt: string | null; soldPrice: number | null; soldOn: string | null };
  /** Photos on the scan row — used to feed the verify-existing flow when
   *  the owner taps Verify now on an unverified quick-post. */
  scanPhotos?: string[];
  /** Owner-typed brand from the scan row. Sent to fig-verify as context. */
  scanBrand?: string | null;
  /** Owner-typed category from the scan row. Sent to fig-verify as context. */
  scanCategory?: string | null;
  /** True when the scan never ran through fig-verify (no `result` blob).
   *  Shows a "Verify now" CTA at the top of the sheet. */
  isUnverified?: boolean;
  /** Render as a full-screen page (with back chevron header) instead of
   *  the default bottom-sheet portal. Use from a dedicated route. */
  mode?: "sheet" | "page";
  onClose: () => void;
  /** Fires after a successful save with the new links + posted state. */
  onSaved?: (next: {
    links: MarketplaceLink[];
    posted: boolean;
    sold?: { soldAt: string | null; soldPrice: number | null; soldOn: string | null };
    deleted?: boolean;
  }) => void;
}

export function ListingStatusSheet({ open, scanId, scanTitle, initialLinks, initialDeadstock = false, initialSold, scanPhotos, scanBrand, scanCategory, isUnverified, mode = "sheet", onClose, onSaved }: Props) {
  const nav = useNavigate();
  const [links, setLinks] = useState<MarketplaceLink[]>(initialLinks);
  const [isDeadstock, setIsDeadstock] = useState<boolean>(initialDeadstock);
  const [saving, setSaving] = useState(false);
  const [unposting, setUnposting] = useState(false);
  // The signed-in collector's saved marketplace usernames, pulled from
  // profiles.marketplace_profiles. Used to auto-append their handle when
  // they add a marketplace chip here so they don't have to retype it.
  const [profileLinks, setProfileLinks] = useState<MarketplaceLink[]>([]);
  // Sold form state
  const [soldOpen, setSoldOpen] = useState(false);
  const [soldPriceInput, setSoldPriceInput] = useState<string>("");
  const [soldOnInput, setSoldOnInput] = useState<string>("");
  const [markingSold, setMarkingSold] = useState(false);
  const [unmarkingSold, setUnmarkingSold] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isSold = !!initialSold?.soldAt;

  // Re-seed local draft each time the sheet opens — avoids stale state when
  // the collector reopens after another edit.
  useEffect(() => {
    if (open) {
      setLinks(initialLinks.length > 0 ? initialLinks : []);
      setIsDeadstock(initialDeadstock);
      setSoldOpen(false);
      setSoldPriceInput(initialSold?.soldPrice != null ? String(initialSold.soldPrice) : "");
      setSoldOnInput(initialSold?.soldOn ?? initialLinks[0]?.name ?? "");
    }
  }, [open, initialLinks, initialDeadstock, initialSold]);

  // Load the collector's saved shop URLs once per open so we can pull the
  // username segment back out when they add a marketplace below.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth.user?.id;
      if (!uid) return;
      const { data } = await supabase
        .from("profiles")
        .select("marketplace_profiles")
        .eq("id", uid)
        .maybeSingle();
      if (cancelled) return;
      const raw = (data as unknown as { marketplace_profiles?: unknown } | null)?.marketplace_profiles;
      setProfileLinks(parseMarketplaceLinks(raw));
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Build the prefilled URL for a freshly-added marketplace chip. Prefers
  // the collector's saved shop URL from Settings (so their username is
  // auto-appended), falls back to just the canonical prefix.
  const prefillFor = (name: string): string => {
    const saved = profileLinks.find((l) => l.name.toLowerCase() === name.toLowerCase());
    if (saved?.url) return saved.url;
    return urlPrefixFor(name);
  };

  // Lock body scroll while open (matches CommentsSheet pattern).
  // In page mode the user is already on a dedicated route — no underlying
  // page to keep still — so skip the lock.
  useEffect(() => {
    if (!open || mode === "page") return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open, mode]);

  if (!open && mode === "sheet") return null;

  const addRow = (name: string) => {
    if (links.some((l) => l.name.toLowerCase() === name.toLowerCase())) {
      toast.info(`${name} already added`);
      return;
    }
    // Pre-fill the URL using (in order):
    //   1. The collector's saved shop URL for this marketplace (from
    //      Settings → marketplace_profiles), so their username is already
    //      filled in and they only need to append the listing slug.
    //   2. The canonical shop prefix as a fallback when no saved profile
    //      exists for this marketplace yet.
    setLinks((cur) => [...cur, { name, url: prefillFor(name) }]);
  };

  const updateUrl = (idx: number, url: string) => {
    setLinks((cur) => cur.map((l, i) => (i === idx ? { ...l, url } : l)));
  };

  const removeRow = (idx: number) => {
    setLinks((cur) => cur.filter((_, i) => i !== idx));
  };

  const save = async () => {
    if (!scanId) return;
    // Validate URLs first — empty is fine, malformed is not.
    const bad = links.find((l) => l.url && !isValidUrl(l.url));
    if (bad) {
      toast.error(`That ${bad.name} link doesn't look like a URL`);
      return;
    }
    setSaving(true);
    // Persist as cleaned-up rows. Keep `posted_to` in sync (first marketplace
    // becomes the primary) so existing read paths that haven't been migrated
    // yet still show the right name.
    const cleaned: MarketplaceLink[] = links.map((l) => ({
      name: l.name,
      url: l.url ? ensureUrlProtocol(l.url) : "",
    }));
    const primary = cleaned[0]?.name ?? null;
    // `posted` drives the closet's KEPT vs LISTED badge. Adding any
    // marketplace link flips the item from kept → listed; removing the
    // last one flips it back to kept. Previously this update only wrote
    // marketplace_links + posted_to, leaving `posted` stuck at false so
    // newly-listed items kept showing the KEPT badge.
    const willBePosted = cleaned.length > 0;
    // The auto-generated `scans` Update type only refreshes after the
    // user approves the migration in their dashboard. Cast through `unknown`
    // so this still compiles in the meantime — the runtime shape is correct.
    const update = {
      marketplace_links: cleaned,
      posted_to: primary ? primary.toLowerCase() : null,
      posted: willBePosted,
      is_deadstock: isDeadstock,
    };
    const { error } = await supabase
      .from("scans")
      .update(update as unknown as Record<string, never>)
      .eq("id", scanId);
    setSaving(false);
    if (error) {
      toast.error("Couldn't save — try again");
      return;
    }
    if (cleaned.length === 0) {
      toast.success("Marked as kept");
    } else {
      const names = cleaned.map((l) => l.name).join(", ");
      toast.success("Links & marketplaces saved", {
        description: `Listed on ${names}.`,
      });
    }
    onSaved?.({ links: cleaned, posted: willBePosted });
    onClose();
  };

  const unpost = async () => {
    if (!scanId) return;
    if (!confirm("Hide this from the public feed? It stays in your closet.")) return;
    setUnposting(true);
    const { error } = await supabase
      .from("scans")
      .update({ posted: false })
      .eq("id", scanId);
    setUnposting(false);
    if (error) { toast.error("Couldn't unpost"); return; }
    toast.success("Hidden from feed — still in your closet");
    onSaved?.({ links, posted: false });
    onClose();
  };

  const markSold = async () => {
    if (!scanId) return;
    const priceNum = soldPriceInput.trim() === "" ? null : Number(soldPriceInput);
    if (priceNum != null && (!Number.isFinite(priceNum) || priceNum < 0)) {
      toast.error("Enter a valid sold price");
      return;
    }
    setMarkingSold(true);
    const update = {
      status: "sold",
      sold_at: new Date().toISOString(),
      sold_price: priceNum,
      posted_to: soldOnInput.trim() ? soldOnInput.trim().toLowerCase() : null,
    };
    const { error } = await supabase
      .from("scans")
      .update(update as unknown as Record<string, never>)
      .eq("id", scanId);
    setMarkingSold(false);
    if (error) { toast.error("Couldn't mark as sold"); return; }
    toast.success("Marked as sold 🎉");
    onSaved?.({
      links,
      posted: true,
      sold: { soldAt: update.sold_at, soldPrice: priceNum, soldOn: update.posted_to },
    });
    onClose();
  };

  const unmarkSold = async () => {
    if (!scanId) return;
    if (!confirm("Move this back to your active listings?")) return;
    setUnmarkingSold(true);
    const update = {
      status: "kept",
      sold_at: null,
      sold_price: null,
    };
    const { error } = await supabase
      .from("scans")
      .update(update as unknown as Record<string, never>)
      .eq("id", scanId);
    setUnmarkingSold(false);
    if (error) { toast.error("Couldn't update"); return; }
    toast.success("Back in your active closet");
    onSaved?.({ links, posted: true, sold: { soldAt: null, soldPrice: null, soldOn: null } });
    onClose();
  };

  const deleteItem = async () => {
    if (!scanId) return;
    setDeleting(true);
    const { error } = await supabase.from("scans").delete().eq("id", scanId);
    setDeleting(false);
    if (error) { toast.error("Couldn't delete — try again"); return; }
    toast.success("Item deleted");
    onSaved?.({ links, posted: false, deleted: true });
    onClose();
  };

  const remaining = MARKETPLACE_NAMES.filter(
    (m) => !links.some((l) => l.name.toLowerCase() === m.toLowerCase()),
  );

  // Header — in sheet mode the title block sits next to an X close button;
  // in page mode a back chevron leads the title and there's no close button.
  const headerBlock = (
    <div style={{ padding: "16px 18px 12px", borderBottom: "1px dashed hsl(var(--sand-deep))", display: "flex", alignItems: "flex-start", gap: 10 }}>
      {mode === "page" && (
        <button
          onClick={onClose}
          aria-label="Back"
          style={{
            background: "none", border: "1px solid hsl(var(--ink))",
            padding: 6, cursor: "pointer", color: "hsl(var(--ink))",
            width: 32, height: 32, flexShrink: 0,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}
        >
          {Icon.chev(16, "l")}
        </button>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="caps" style={{ color: "hsl(var(--rust))" }}>Listing status</div>
        <div style={{
          fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 18,
          color: "hsl(var(--ink))", marginTop: 2, lineHeight: 1.15,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {scanTitle ?? "This scan"}
        </div>
        <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 12, color: "hsl(var(--ink-2))", marginTop: 4 }}>
          {links.length === 0
            ? "Empty list = shown as Kept on your feed."
            : "Each marketplace becomes a tappable badge for shoppers."}
        </div>
      </div>
      {mode === "sheet" && (
        <button
          onClick={onClose}
          aria-label="Close"
          style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}
        >
          {Icon.x ? Icon.x(16) : <span style={{ fontSize: 18 }}>×</span>}
        </button>
      )}
    </div>
  );

  // Inner panel JSX — shared by both shells. In sheet mode it's wrapped
  // in a portal'd backdrop; in page mode it renders directly into the
  // route (parent supplies MobileFrame). The inner panel's own styling
  // (maxWidth 440, maxHeight 100dvh, flex column) works for both.
  //
  // Portal background — MobileFrame's page-content wrapper sets
  // `position: relative; zIndex: 5`, creating a local stacking context.
  // Without the portal, this sheet's zIndex 50 only competes inside that
  // context (5) and the BottomDock at root-level zIndex 50 paints over
  // the bottom of the sheet ("hidden under the nav bar" symptom).
  const innerPanel = (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: "100%", maxWidth: 440,
        background: "hsl(var(--cream))",
        borderTop: mode === "sheet" ? "1px solid hsl(var(--ink))" : "none",
        // iOS Capacitor includes the status-bar area in 100vh, so a flat
        // 85vh let the top of a tall sheet sit BEHIND the iOS status bar
        // — header title + close button were clipped. Use dynamic viewport
        // (100dvh) and explicitly subtract the safe-area-inset-top + a
        // small gutter so the sheet's top edge always sits below the
        // status bar on every device. In page mode the panel just fills
        // its parent (MobileFrame already constrains height + safe areas).
        maxHeight: mode === "sheet"
          ? "calc(100dvh - env(safe-area-inset-top, 0px) - 16px)"
          : undefined,
        minHeight: mode === "page" ? "100%" : undefined,
        display: "flex", flexDirection: "column",
      }}
    >
        {headerBlock}

        {/* Body — own scroll container. overscroll-behavior contain
            stops any chained scroll from reaching MobileFrame's
            scrollable inner div behind the sheet. touch-action pan-y
            re-enables vertical pan inside the sheet (the overlay sets
            touch-action: none to block backdrop drags). minHeight: 0
            on the flex child is the iOS Safari fix — without it the
            child takes its natural height instead of flexing into the
            available 85vh, so the inner overflow:auto has nothing to
            scroll against. */}
        <div style={{
          padding: "12px 18px 14px",
          // Sheet mode: own scroll container (sheet has a hard maxHeight).
          // Page mode: let MobileFrame's outer scroll handle overflow so
          // the back chevron + footer don't get clipped on tall content.
          overflowY: mode === "sheet" ? "auto" : "visible",
          flex: mode === "sheet" ? 1 : undefined,
          minHeight: mode === "sheet" ? 0 : undefined,
          overscrollBehavior: mode === "sheet" ? "contain" : undefined,
          touchAction: mode === "sheet" ? "pan-y" : undefined,
          WebkitOverflowScrolling: mode === "sheet" ? "touch" : undefined,
        }}>
          {/* Verify-now CTA at the very top of the sheet body for
              quick-posted items that never ran through fig-verify.
              Stashes the post's id + photos + meta in sessionStorage,
              closes the sheet, then routes to /analyze where the
              verify-existing branch fetches the photos and runs the
              verification against the same scan row. */}
          {isUnverified && scanId && scanPhotos && scanPhotos.length > 0 && (
            <button
              type="button"
              onClick={() => {
                try {
                  sessionStorage.setItem("fig_verify_existing_id", scanId);
                  sessionStorage.setItem("fig_verify_existing_photos", JSON.stringify(scanPhotos));
                  sessionStorage.setItem("fig_verify_existing_meta", JSON.stringify({
                    title: scanTitle ?? "",
                    brand: scanBrand ?? "",
                    category: scanCategory ?? "",
                  }));
                } catch { /* quota */ }
                // Soft-navigate to the optional add-photos step.
                // window.location.href used to live here, but on iOS
                // native that triggers a full WebView reload, which
                // re-runs initializeDeepLinks() — the original OAuth
                // launch URL gets re-processed and Supabase throws a
                // PKCE error because the code_verifier was already
                // consumed by the first exchange. React Router's nav
                // keeps the SPA mounted and avoids the re-init.
                if (mode === "sheet") onClose();
                nav("/verify-existing");
              }}
              style={{
                width: "100%", marginBottom: 14,
                background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))",
                border: "1.5px solid hsl(var(--ink))", padding: "12px",
                cursor: "pointer", textAlign: "left",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}
            >
              <span>
                <span style={{
                  display: "block",
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
                  letterSpacing: "0.14em", textTransform: "uppercase",
                  opacity: 0.7,
                }}>Not yet verified</span>
                <span style={{
                  display: "block", marginTop: 2,
                  fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 14,
                }}>Verify now →</span>
              </span>
            </button>
          )}

          {isSold && (
            <div
              style={{
                border: "1.5px solid hsl(var(--rust))",
                background: "hsl(var(--paper-2, var(--cream)))",
                padding: "12px 14px",
                marginBottom: 14,
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div className="caps" style={{ color: "hsl(var(--rust))" }}>Sold</div>
                <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 14, color: "hsl(var(--ink))", marginTop: 2 }}>
                  {initialSold?.soldPrice != null ? `$${Number(initialSold.soldPrice).toFixed(0)}` : "Price not set"}
                  {initialSold?.soldOn ? <> · on {initialSold.soldOn}</> : null}
                </div>
              </div>
              <button
                onClick={unmarkSold}
                disabled={unmarkingSold}
                style={{
                  background: "transparent", border: "1px dashed hsl(var(--ink-2))",
                  padding: "6px 10px", cursor: unmarkingSold ? "wait" : "pointer",
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
                  letterSpacing: "0.1em", textTransform: "uppercase",
                  color: "hsl(var(--ink-2))", opacity: unmarkingSold ? 0.6 : 1,
                  whiteSpace: "nowrap",
                }}
              >{unmarkingSold ? "Updating…" : "Unmark"}</button>
            </div>
          )}

          {links.length === 0 && (
            <div style={{
              border: "1px dashed hsl(var(--ink))", padding: "14px",
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontSize: 13, color: "hsl(var(--ink-2))", textAlign: "center",
            }}>
              Not listed anywhere — this shows as <strong style={{ fontStyle: "normal" }}>Kept</strong>. Add a marketplace below if you list it.
            </div>
          )}

          {links.map((l, idx) => {
            const lower = l.name.toLowerCase();
            const isInPerson = lower === "in person";
            const isPersonalSite = lower === "personal site";
            // The "In person" row accepts either a typed address (rendered as
            // a non-clickable label on Post) or a Google/Apple Maps share URL
            // (rendered as a clickable "open in maps" CTA). Personal Site is
            // a plain URL like any other marketplace.
            const placeholder = isInPerson
              ? "123 Main St, Brooklyn NY  or  https://maps.app.goo.gl/…"
              : isPersonalSite
              ? "https://yourshop.com"
              : urlPrefixFor(l.name)
              ? `${urlPrefixFor(l.name)}your-username`
              : `https://${l.name.toLowerCase()}.com/your-listing`;
            const helper = isInPerson
              ? "Optional — paste an address or a Google/Apple Maps share link."
              : isPersonalSite
              ? "Optional — paste your full site URL."
              : "Optional — paste it later if you don't have the URL yet.";
            return (
            <div key={l.name + idx} style={{ marginTop: idx === 0 ? 0 : 10, border: "1px solid hsl(var(--ink))", padding: "10px 12px", background: "hsl(var(--paper-2, var(--cream)))" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <div className="caps" style={{ color: "hsl(var(--ink))", fontSize: 11 }}>{l.name}</div>
                <button
                  onClick={() => removeRow(idx)}
                  aria-label={`Remove ${l.name}`}
                  style={{
                    background: "transparent", border: "1px dashed hsl(var(--ink-2))",
                    padding: "3px 8px", cursor: "pointer",
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
                    letterSpacing: "0.1em", textTransform: "uppercase",
                    color: "hsl(var(--ink-2))",
                  }}
                >Remove</button>
              </div>
              <input
                // In-person allows plain-text addresses, so don't force type=url
                // which some mobile keyboards harden against spaces/commas.
                type={isInPerson ? "text" : "url"}
                inputMode={isInPerson ? "text" : "url"}
                placeholder={placeholder}
                value={l.url}
                onChange={(e) => updateUrl(idx, e.target.value)}
                style={{
                  width: "100%", marginTop: 6,
                  background: "hsl(var(--cream))",
                  border: "1px solid hsl(var(--sand-deep))",
                  padding: "8px 10px",
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                  color: "hsl(var(--ink))",
                }}
              />
              {(() => {
                // Soft per-marketplace domain hint. Skip for In Person and
                // Personal Site, which accept any input — In Person can be
                // free text, Personal Site can be any domain the seller owns.
                if (isInPerson || isPersonalSite) {
                  return (
                    <div
                      style={{
                        marginTop: 4,
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: 9,
                        color: "hsl(var(--ink-2))",
                        letterSpacing: "0.06em",
                      }}
                    >
                      {helper}
                    </div>
                  );
                }
                const warn = urlDomainWarning(l.name, l.url);
                return (
                  <div
                    style={{
                      marginTop: 4,
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 9,
                      color: warn ? "hsl(var(--rust))" : "hsl(var(--ink-2))",
                      letterSpacing: "0.06em",
                    }}
                  >
                    {warn ?? helper}
                  </div>
                );
              })()}
            </div>
            );
          })}

          {/* Add marketplace */}
          {remaining.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div className="caps" style={{ color: "hsl(var(--ink-2))", marginBottom: 6 }}>Add marketplace</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {remaining.map((m) => (
                  <button
                    key={m}
                    onClick={() => addRow(m)}
                    style={{
                      background: "transparent", border: "1px solid hsl(var(--ink))",
                      padding: "6px 10px", cursor: "pointer",
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                      letterSpacing: "0.08em", textTransform: "uppercase",
                      color: "hsl(var(--ink))",
                    }}
                  >+ {m}</button>
                ))}
              </div>
            </div>
          )}

          {/* Deadstock toggle — counts toward the It's Deadstock badge. */}
          <label
            style={{
              display: "flex", alignItems: "flex-start", gap: 10,
              marginTop: 14, padding: "10px 12px",
              border: "1px dashed hsl(var(--sand-deep))",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={isDeadstock}
              onChange={(e) => setIsDeadstock(e.target.checked)}
              style={{ marginTop: 2, flexShrink: 0 }}
            />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{
                display: "block",
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontWeight: 700, fontSize: 14, color: "hsl(var(--ink))",
                lineHeight: 1.25,
              }}>
                This is deadstock
              </span>
              <span style={{
                display: "block",
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontStyle: "italic", fontSize: 12,
                color: "hsl(var(--ink-2))",
                marginTop: 2, lineHeight: 1.35,
              }}>
                Unworn, tags attached. Counts toward your It's Deadstock badge.
              </span>
            </span>
          </label>

          {/* Inline save — keeps the collector close to the links they just edited */}
          <button
            onClick={save}
            disabled={saving}
            style={{
              width: "100%", marginTop: 14,
              background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))",
              border: "1.5px solid hsl(var(--ink))", padding: "12px",
              cursor: saving ? "wait" : "pointer",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
              letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600,
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? "Saving…" : links.length === 0 ? "Save (mark as kept)" : "Save links & marketplaces"}
          </button>

          {/* Unpost — destructive-ish, kept visually quiet */}
          <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px dashed hsl(var(--sand-deep))" }}>
            {/* Mark as sold — for items that sold off-platform */}
            {!isSold && (
              <div style={{ marginBottom: 14 }}>
                {!soldOpen ? (
                  <button
                    onClick={() => setSoldOpen(true)}
                    style={{
                      // Use the auto-derived --marigold-fg (guaranteed AA on
                      // --marigold across every theme) instead of --ink, which
                      // collides with marigold on Trapper Keeper (deep teal
                      // marigold + deep berry ink) and Velvet Underground.
                      width: "100%", background: "hsl(var(--marigold))",
                      color: "hsl(var(--marigold-fg))", border: "2px solid hsl(var(--ink))",
                      padding: "12px", cursor: "pointer",
                      fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                      letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 700,
                    }}
                  >Mark as sold</button>
                ) : (
                  <div style={{ border: "1px solid hsl(var(--ink))", padding: "12px", background: "hsl(var(--paper-2, var(--cream)))" }}>
                    <div className="caps" style={{ color: "hsl(var(--rust))" }}>Mark as sold</div>
                    <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 12, color: "hsl(var(--ink-2))", marginTop: 4, marginBottom: 10 }}>
                      Moves this to your Sold tab. Stays visible in your closet.
                    </div>
                    <label className="caps" style={{ color: "hsl(var(--ink-2))", display: "block", marginBottom: 4 }}>Sold price</label>
                    <input
                      type="number"
                      inputMode="decimal"
                      placeholder="120"
                      value={soldPriceInput}
                      onChange={(e) => setSoldPriceInput(e.target.value)}
                      style={{
                        width: "100%", background: "hsl(var(--cream))",
                        border: "1px solid hsl(var(--sand-deep))",
                        padding: "8px 10px",
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
                        color: "hsl(var(--ink))",
                      }}
                    />
                    <label className="caps" style={{ color: "hsl(var(--ink-2))", display: "block", marginTop: 10, marginBottom: 4 }}>Sold on (optional)</label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                      {MARKETPLACE_NAMES.map((m) => {
                        const active = soldOnInput.toLowerCase() === m.toLowerCase();
                        return (
                          <button
                            key={m}
                            onClick={() => setSoldOnInput(active ? "" : m)}
                            style={{
                              background: active ? "hsl(var(--ink))" : "transparent",
                              color: active ? "hsl(var(--cream))" : "hsl(var(--ink))",
                              border: "1px solid hsl(var(--ink))",
                              padding: "5px 9px", cursor: "pointer",
                              fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                              letterSpacing: "0.08em", textTransform: "uppercase",
                            }}
                          >{m}</button>
                        );
                      })}
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                      <button
                        onClick={() => setSoldOpen(false)}
                        style={{
                          flex: 1, background: "transparent",
                          border: "1px solid hsl(var(--ink))", padding: "10px",
                          cursor: "pointer", color: "hsl(var(--ink))",
                          fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                          letterSpacing: "0.1em", textTransform: "uppercase",
                        }}
                      >Cancel</button>
                      <button
                        onClick={markSold}
                        disabled={markingSold}
                        style={{
                          flex: 2, background: "hsl(var(--rust))", color: "hsl(var(--rust-fg))",
                          border: "1px solid hsl(var(--rust))", padding: "10px",
                          cursor: markingSold ? "wait" : "pointer",
                          fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                          letterSpacing: "0.1em", textTransform: "uppercase",
                          opacity: markingSold ? 0.7 : 1,
                        }}
                      >{markingSold ? "Saving…" : "Confirm sold"}</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={unpost}
              disabled={unposting}
              style={{
                width: "100%", background: "transparent",
                color: "hsl(var(--rust))", border: "1.5px solid hsl(var(--rust))",
                padding: "10px", cursor: unposting ? "wait" : "pointer",
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600,
                opacity: unposting ? 0.6 : 1,
              }}
            >
              {unposting ? "Hiding…" : "Hide from feed (keep in closet)"}
            </button>
            <div style={{ marginTop: 6, fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 11, color: "hsl(var(--ink-2))", textAlign: "center", lineHeight: 1.35 }}>
              Stays in your closet. Reactions and notes are preserved if you re-post later.
            </div>

            {/* Delete item — fully destructive, requires confirm */}
            <div style={{ marginTop: 14 }}>
              {!confirmingDelete ? (
                <button
                  onClick={() => setConfirmingDelete(true)}
                  style={{
                    width: "100%", background: "hsl(var(--rust))", color: "hsl(var(--rust-fg))",
                    border: "2px solid hsl(var(--rust))", padding: "10px",
                    cursor: "pointer",
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                    letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 700,
                  }}
                >Delete item</button>
              ) : (
                <div style={{ border: "1.5px solid hsl(var(--rust))", padding: "12px", background: "hsl(var(--paper-2, var(--cream)))" }}>
                  <div className="caps" style={{ color: "hsl(var(--rust))" }}>Are you sure?</div>
                  <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 12, color: "hsl(var(--ink-2))", marginTop: 4, marginBottom: 10, lineHeight: 1.35 }}>
                    This permanently deletes the item from your closet. This cannot be undone.
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={() => setConfirmingDelete(false)}
                      disabled={deleting}
                      style={{
                        flex: 1, background: "transparent",
                        border: "1px solid hsl(var(--ink))", padding: "10px",
                        cursor: deleting ? "wait" : "pointer", color: "hsl(var(--ink))",
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                        letterSpacing: "0.1em", textTransform: "uppercase",
                      }}
                    >No</button>
                    <button
                      onClick={deleteItem}
                      disabled={deleting}
                      style={{
                        flex: 1, background: "hsl(var(--rust))", color: "hsl(var(--rust-fg))",
                        border: "1px solid hsl(var(--rust))", padding: "10px",
                        cursor: deleting ? "wait" : "pointer",
                        fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                        letterSpacing: "0.1em", textTransform: "uppercase",
                        opacity: deleting ? 0.7 : 1,
                      }}
                    >{deleting ? "Deleting…" : "Yes, delete"}</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 18px 18px", borderTop: "1px solid hsl(var(--ink))", display: "flex", gap: 8 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, background: "transparent",
              border: "1px solid hsl(var(--ink))", padding: "12px",
              cursor: "pointer", color: "hsl(var(--ink))",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
              letterSpacing: "0.1em", textTransform: "uppercase",
            }}
          >Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            style={{
              flex: 2, background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))",
              border: "1px solid hsl(var(--ink))", padding: "12px",
              cursor: saving ? "wait" : "pointer",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
              letterSpacing: "0.1em", textTransform: "uppercase",
              opacity: saving ? 0.7 : 1,
            }}
          >{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
  );

  if (mode === "page") {
    return (
      <div className="paper-bg" style={{ minHeight: "100%", display: "flex", flexDirection: "column", background: "hsl(var(--cream))" }}>
        {innerPanel}
      </div>
    );
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Listing status"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 100,
        background: "hsla(0,0%,0%,0.45)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
        touchAction: "none",
      }}
    >
      {innerPanel}
    </div>,
    document.body,
  );
}

// Re-export the parser so callers can normalise raw scan rows into the
// component's expected shape without importing the lib themselves.
export { parseMarketplaceLinks };
